import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  BarChart3
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SellerFinance = () => {
  const [financialRecords, setFinancialRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  // Mock data
  const mockFinancialRecords = [
    {
      id: 1,
      type: 'income',
      title: 'Doanh thu tháng 12/2024',
      amount: 15000000,
      currency: 'VND',
      date: '2024-12-15',
      status: 'completed',
      description: 'Doanh thu từ các đơn hàng tháng 12',
      category: 'sales',
      orderCount: 45,
      profit: 8500000
    },
    {
      id: 2,
      type: 'expense',
      title: 'Chi phí vận chuyển',
      amount: 2500000,
      currency: 'VND',
      date: '2024-12-14',
      status: 'pending',
      description: 'Chi phí vận chuyển các đơn hàng',
      category: 'shipping',
      orderCount: 12,
      profit: -2500000
    },
    {
      id: 3,
      type: 'income',
      title: 'Thanh toán đơn hàng ORD-001',
      amount: 3500000,
      currency: 'VND',
      date: '2024-12-13',
      status: 'completed',
      description: 'Thanh toán cho đơn hàng thiết kế',
      category: 'design',
      orderCount: 1,
      profit: 2800000
    },
    {
      id: 4,
      type: 'expense',
      title: 'Chi phí nguyên vật liệu',
      amount: 1800000,
      currency: 'VND',
      date: '2024-12-12',
      status: 'completed',
      description: 'Mua nguyên vật liệu cho sản xuất',
      category: 'materials',
      orderCount: 8,
      profit: -1800000
    }
  ];

  useEffect(() => {
    setFinancialRecords(mockFinancialRecords);
    setFilteredRecords(mockFinancialRecords);
  }, []);

  useEffect(() => {
    filterRecords();
  }, [searchTerm, statusFilter, dateFilter, financialRecords]);

  const filterRecords = () => {
    let filtered = financialRecords;

    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    if (dateFilter !== 'all') {
      const today = new Date();
      const filteredDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate.toDateString() === today.toDateString();
          });
          break;
        case 'week':
          filteredDate.setDate(today.getDate() - 7);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= filteredDate;
          });
          break;
        case 'month':
          filteredDate.setMonth(today.getMonth() - 1);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= filteredDate;
          });
          break;
      }
    }

    setFilteredRecords(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    return type === 'income' ? 'text-green-600' : 'text-red-600';
  };

  const getTypeIcon = (type) => {
    return type === 'income' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const handleAddRecord = () => {
    setShowAddModal(true);
  };

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
  };

  const calculateTotalIncome = () => {
    return filteredRecords
      .filter(record => record.type === 'income')
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const calculateTotalExpense = () => {
    return filteredRecords
      .filter(record => record.type === 'expense')
      .reduce((sum, record) => sum + record.amount, 0);
  };

  const calculateNetProfit = () => {
    return calculateTotalIncome() - calculateTotalExpense();
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-400 to-blue-400 rounded-lg shadow-lg mb-6 p-6 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
          <div>
            <h1 className="text-2xl font-bold mb-2">Quản lý tài chính</h1>
            <p className="text-green-100">Theo dõi thu chi và báo cáo tài chính</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4 lg:mt-0">
            <button
              onClick={handleAddRecord}
              className="flex items-center space-x-2 bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Thêm báo cáo</span>
            </button>
            <button
              onClick={handleGenerateReport}
              className="flex items-center space-x-2 bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Báo cáo tài chính</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tổng thu</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(calculateTotalIncome())}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tổng chi</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(calculateTotalExpense())}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Lợi nhuận</p>
              <p className={`text-2xl font-bold ${calculateNetProfit() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(calculateNetProfit())}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Tìm kiếm báo cáo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="completed">Hoàn thành</option>
              <option value="pending">Đang xử lý</option>
              <option value="cancelled">Đã hủy</option>
            </select>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tất cả thời gian</option>
              <option value="today">Hôm nay</option>
              <option value="week">Tuần này</option>
              <option value="month">Tháng này</option>
            </select>
          </div>
        </div>
      </div>

      {/* Financial Records Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-green-300 to-blue-300">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Báo cáo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Số tiền
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Ngày
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{record.title}</div>
                      <div className="text-sm text-gray-500">{record.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(record.type)}
                      <span className={`text-sm font-medium ${getTypeColor(record.type)}`}>
                        {record.type === 'income' ? 'Thu' : 'Chi'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${getTypeColor(record.type)}`}>
                      {formatCurrency(record.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(record.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                      {record.status === 'completed' ? 'Hoàn thành' : 
                       record.status === 'pending' ? 'Đang xử lý' : 'Đã hủy'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(record)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Record Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Thêm báo cáo tài chính</h3>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tiêu đề</label>
                  <input
                    type="text"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Nhập tiêu đề báo cáo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Loại</label>
                  <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500">
                    <option value="income">Thu</option>
                    <option value="expense">Chi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Số tiền</label>
                  <input
                    type="number"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder="Nhập số tiền"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mô tả</label>
                  <textarea
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    rows="3"
                    placeholder="Nhập mô tả"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                  >
                    Thêm
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Financial Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Báo cáo tài chính</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Đóng</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-green-800 mb-2">Tổng thu</h4>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(calculateTotalIncome())}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-red-800 mb-2">Tổng chi</h4>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(calculateTotalExpense())}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Lợi nhuận ròng</h4>
                <p className={`text-3xl font-bold ${calculateNetProfit() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(calculateNetProfit())}
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Đóng
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center space-x-2">
                  <Download className="w-4 h-4" />
                  <span>Tải xuống báo cáo</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Details Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chi tiết báo cáo</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tiêu đề</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRecord.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Loại</label>
                  <p className={`mt-1 text-sm font-medium ${getTypeColor(selectedRecord.type)}`}>
                    {selectedRecord.type === 'income' ? 'Thu' : 'Chi'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Số tiền</label>
                  <p className={`mt-1 text-lg font-bold ${getTypeColor(selectedRecord.type)}`}>
                    {formatCurrency(selectedRecord.amount)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ngày</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedRecord.date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mô tả</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRecord.description}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRecord.status)}`}>
                    {selectedRecord.status === 'completed' ? 'Hoàn thành' : 
                     selectedRecord.status === 'pending' ? 'Đang xử lý' : 'Đã hủy'}
                  </span>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerFinance; 